import makeStyles from '@mui/styles/makeStyles';
import {Check} from "lucide-react";
import {get, isEmpty} from 'lodash';
import React from "react";
import {useTranslation} from "react-i18next";
import {isSurveyComplete, isSurveyPartial, surveyCompletedOn} from "business/survey";
import logo from 'common/assets/logo.png';
import {convertUrl} from "common/helpers";
import {color, font, radius, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import Card from "components/redesign/Card";
import CtaButton from "components/redesign/CtaButton";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: 18,
    height: '100%',
    boxSizing: 'border-box',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  iconTile: {
    width: 40,
    height: 40,
    borderRadius: radius.CONTROL,
    background: color.ACCENT_TINT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    width: 24,
    height: 24,
  },
  title: {
    ...type.sessionTitle,
    color: color.TEXT,
    margin: 0,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  description: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '12px 0 0',
  },
  action: {
    marginTop: 'auto',
    paddingTop: 18,
  },
  completed: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  check: {
    width: 22,
    height: 22,
    borderRadius: radius.PILL,
    background: color.SUCCESS,
    color: color.ON_DANGER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  completedText: {
    ...type.meta,
    color: color.SUCCESS,
    margin: 0,
  },
});

const SurveyCard = ({survey, onOpen}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const isComplete = isSurveyComplete(survey);
  const isPartial = isSurveyPartial(survey);
  const icon = isEmpty(survey.icon) ? logo : convertUrl(survey.icon);
  const description = st(get(survey, 'shortDescription'), get(survey, 'localeShortDescription'));

  return (
    <Card variant={'media'} className={classes.card}>
      <div className={classes.titleRow}>
        <span className={classes.iconTile}>
          <ShimmerImage src={icon} fit={'contain'} className={classes.icon}/>
        </span>
        <p className={classes.title}>{st(get(survey, 'name'), get(survey, 'localeName'))}</p>
      </div>

      {Boolean(description) && <p className={classes.description}>{description}</p>}

      <div className={classes.action}>
        {isComplete ? (
          <span className={classes.completed}>
            <span className={classes.check}>
              <Check size={13} strokeWidth={3}/>
            </span>
            <p className={classes.completedText}>{`${t('completedOn')} ${surveyCompletedOn(survey)}`}</p>
          </span>
        ) : (
          <CtaButton
            label={isPartial ? t('finishCaps') : t('takeCaps')}
            variant={isPartial ? 'secondary' : 'primary'}
            size={'control'}
            onClick={onOpen}
          />
        )}
      </div>
    </Card>
  );
};

export default SurveyCard;
